// frontend/src/widgets/MessagingHealthWidget.jsx
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2025
// Contact:      dev@castle-traffic.co.uk
// Created:      2025-12-16
// Updated:      2026-09-08 15:52 BST
//
// Description:
//   Messaging / Channel Health widget.
//   • Will show high-level channel status (Email / SMS / WhatsApp).
//   • Future: last successful send, last error, provider latency,
//     and quick links to System → Integrations.
//
// Lifecycle Status:
//   Active stub.
//
// Mobile Responsiveness (2026-09-08):
//   - Preserves the existing single-column channel-status layout.
//   - Uses natural widget height on mobile while retaining desktop fill behaviour.
//   - Keeps channel/status pills full-width and readable on narrow screens.
//   - Allows long channel/status text to wrap without horizontal overflow.
//   - Converts the placeholder integration notes into a structured mobile-safe list.
//   - Uses one render path for desktop and mobile; no duplicated mobile DOM.
//
// Operational Notes:
//   - Values remain static placeholders.
//   - This widget does not currently query messaging health or provider errors.
//   - No polling, API calls or provider diagnostics are introduced by this pass.
//
// Future Intent:
//   - Query /api/messaging/health for per-channel health.
//   - Surface recent provider errors such as SendGrid / Twilio failures.
//   - Add last successful send, last error, provider latency and integration links.

import React from "react";

import { useTheme } from "../context/ThemeContext.jsx";

const MessagingHealthWidget = () => {
  const { theme } = useTheme() || {};
  const accent =
    theme?.accent_color ||
    "#ff7a00";

  const pillBase =
    "mb-2 flex w-full items-center justify-between gap-3 rounded-lg border px-3 py-2 text-xs";

  return (
    <div className="flex h-auto min-h-0 w-full flex-col overflow-visible md:h-full md:overflow-hidden">
      {/* Header */}
      <div className="mb-3 min-w-0">
        <h3
          className="m-0 break-words text-base font-semibold"
          style={{
            color: accent,
            overflowWrap:
              "anywhere",
          }}
        >
          Messaging Health
        </h3>

        <p
          className="mt-1 mb-0 break-words text-xs leading-5 text-gray-600"
          style={{
            overflowWrap:
              "anywhere",
          }}
        >
          High-level status for each messaging channel.
        </p>
      </div>

      {/* Body */}
      <div className="min-h-0 flex-1 rounded-lg border border-dashed border-gray-300 bg-gray-50 p-3 text-xs leading-5 text-gray-700">
        <div
          className={`${pillBase} border-green-300 bg-green-100 text-green-800`}
        >
          <span className="min-w-0 flex-1 break-words font-medium">
            Email
          </span>

          <span className="shrink-0 font-semibold">
            OK
          </span>
        </div>

        <div
          className={`${pillBase} border-green-300 bg-green-100 text-green-800`}
        >
          <span className="min-w-0 flex-1 break-words font-medium">
            SMS
          </span>

          <span className="shrink-0 font-semibold">
            OK
          </span>
        </div>

        <div
          className={`${pillBase} border-yellow-300 bg-yellow-100 text-yellow-800`}
        >
          <span className="min-w-0 flex-1 break-words font-medium">
            WhatsApp
          </span>

          <span
            className="min-w-0 text-right font-semibold"
            style={{
              overflowWrap:
                "anywhere",
            }}
          >
            Degraded (stub)
          </span>
        </div>

        <div
          className="mt-3 break-words italic text-gray-500"
          style={{
            overflowWrap:
              "anywhere",
          }}
        >
          <p className="m-0">
            Values above are static placeholders. Later this widget will query:
          </p>

          <ul className="mt-2 mb-0 list-disc space-y-1 pl-5">
            <li>
              /api/messaging/health (per-channel)
            </li>

            <li>
              Recent provider errors (SendGrid / Twilio)
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default MessagingHealthWidget;
